'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth-context';
import { operatorQueries } from '@/features/operators';
import { useSelfAssignMutation } from '../services/handover-scanning.mutations';
import { ReceivingListPanel } from './receiving-list-panel';
import { ScannerSelectionPanel } from './scanner-selection-panel';
import { CurrentUserOperatorCard } from './current-user-operator-card';
import { AssignmentConfirmFooter } from './assignment-confirm-footer';

// Flip to `true` if the client decides self-assigners must already have an
// operator_profile (the controller's ensureCallerCanBeOperator() must be
// flipped server-side in tandem).
const MUST_BE_OPERATOR = false;

export function SelfAssignTab() {
  const { user } = useAuth();

  const [selectedContainerIds, setSelectedContainerIds] = useState<number[]>(
    []
  );
  const [selectedScannerId, setSelectedScannerId] = useState<number | null>(
    null
  );
  const [selectedScannerName, setSelectedScannerName] = useState<string | null>(
    null
  );

  const userId = user?.id ?? 0;
  const operatorQuery = useQuery({
    ...operatorQueries.detail(userId),
    retry: false
  });

  const operatorProfileMissing =
    !operatorQuery.isLoading && operatorQuery.data?.data == null;

  const isConfirmDisabled =
    selectedContainerIds.length === 0 ||
    selectedScannerId === null ||
    (MUST_BE_OPERATOR && operatorProfileMissing);

  const selfAssignMutation = useSelfAssignMutation();

  function handleToggleContainer(id: number) {
    setSelectedContainerIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  }

  function handleSelectAll(ids: number[]) {
    setSelectedContainerIds(ids);
  }

  function handleClearSelection() {
    setSelectedContainerIds([]);
  }

  function handleSelectScanner(id: number, name: string) {
    setSelectedScannerId(id);
    setSelectedScannerName(name);
  }

  async function handleConfirm() {
    if (isConfirmDisabled) return;

    try {
      await selfAssignMutation.mutateAsync({
        scanning_container_ids: selectedContainerIds,
        device_id: selectedScannerId!
      });

      setSelectedContainerIds([]);
      setSelectedScannerId(null);
      setSelectedScannerName(null);
    } catch {
      // handled by global MutationCache
    }
  }

  return (
    <div className='flex flex-col pb-24'>
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
        <ReceivingListPanel
          selectedContainerIds={selectedContainerIds}
          onToggleContainer={handleToggleContainer}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
        />

        <ScannerSelectionPanel
          selectedScannerId={selectedScannerId}
          onSelectScanner={handleSelectScanner}
          selectedContainerCount={selectedContainerIds.length}
        />

        <CurrentUserOperatorCard />
      </div>

      <AssignmentConfirmFooter
        selectedContainerCount={selectedContainerIds.length}
        selectedScanner={selectedScannerName}
        selectedOperator={user?.name ?? null}
        disabled={isConfirmDisabled}
        onConfirm={handleConfirm}
        isConfirming={selfAssignMutation.isPending}
      />
    </div>
  );
}
